import React, { useCallback, useState, useEffect, useRef } from "react";
import { Box, Text } from "@thenounproject/lingo-core";
import AutosizeTextarea from "react-textarea-autosize";
import styled from "styled-components";

import { insertData, InsertType } from "@constants/InsertType";
import { CanvasComponentProps, CanvasEditableProps } from "./sharedProps";
import { getVersionedItemId } from "@src/helpers/items";
import { InViewTracker } from "@src/contexts/InViewProvider";
import { SelectionWrapper } from "./SelectionWrapper";
import {
  utils,
  useSubmitTextKeys,
  focusAllOfInput,
  focusEndOfInput,
} from "@thenounproject/lingo-core";
import { OverflowButton } from "./OverflowButton";

const Wrapper = styled(Box)`
  position: relative;
  &:hover {
    .action-button {
      opacity: 1;
    }
  }
`;

const HeadingText = styled(Text).attrs({
  as: "h2",
  font: "gallery.heading",
  color: "black",
  borderRadius: "default",
  px: "xs",
})<{ canEdit?: boolean }>`
  word-break: break-word;
  cursor: ${p => (p.canEdit ? "pointer" : "default")};
  &:first-child {
    margin-top: 0;
  }
`;

const Textarea = styled(AutosizeTextarea)`
  min-height: 42px;
  padding: 0;
  margin: 0;
  border-radius: ${utils.getBorderRadius("default")};
  border: none;
  resize: none;
  width: 100%;
  font-feature-settings: inherit;
  ${p => utils.getFont(null, "gallery.heading", p)}
  &:focus {
    outline: none;
  }
`;

type Props = CanvasComponentProps & CanvasEditableProps;

const CanvasHeading: React.FC<Props> = ({
  item,
  isSelected,
  onClick,
  onDoubleClick: _onDoubleClick,
  onOverflowSelected,
  onDragEnd,
  onDragStart,
  draggable,
  canEdit,
  isEditing,
  onFinishEditing,
  onBeginEditing,
  index,
  ...rest
}) => {
  const [editValue, setEditValue] = useState(item.data.content);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const onKeyDown = useSubmitTextKeys(false);

  const versionedId = getVersionedItemId(item);
  const hasDefaultValue = item.data.content === insertData[InsertType.heading].data.content;

  useEffect(() => {
    setEditValue(item.data.content);
    if (isEditing && editorRef.current) {
      (hasDefaultValue ? focusAllOfInput : focusEndOfInput)(editorRef.current);
    }
  }, [isEditing, item.data.content, hasDefaultValue]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick(item, e);
    },
    [onClick, item]
  );

  const handleDoubleClick = useCallback(() => {
    if (canEdit) onBeginEditing?.(item.id);
  }, [canEdit, onBeginEditing, item.id]);

  const handleBlur = useCallback(() => {
    if (!isEditing || !canEdit || !onFinishEditing) return;
    const val = editValue.trim();
    if (item.data.content === val) {
      onFinishEditing(item, null);
    } else {
      onFinishEditing(item, { data: { content: val } });
    }
  }, [isEditing, canEdit, onFinishEditing, item, editValue]);

  const handleOverflow = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOverflowSelected(item);
    },
    [item, onOverflowSelected]
  );

  return (
    <Wrapper
      width="100%" // always full width
      data-droppable-item
      data-full-width
      data-item-index={index}
      data-item-id={item.id}
      style={{ flexShrink: 0, flexGrow: 0 }}
      {...rest}>
      <Box
        id={item.id}
        data-itemid={versionedId}
        data-dragid={versionedId}
        data-shortid={item.shortId}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        position="relative">
        <InViewTracker id={item.shortId} type="header" />
        <SelectionWrapper selected={isSelected}>
          <Box flex="1 1 100%" borderRadius="default" onClick={handleClick}>
            {isEditing ? (
              <Textarea
                aria-label="Update heading text"
                ref={editorRef}
                value={editValue}
                onBlur={handleBlur}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={onKeyDown}
              />
            ) : (
              <HeadingText canEdit={canEdit} onDoubleClick={handleDoubleClick}>
                {item.data.content}
              </HeadingText>
            )}
          </Box>
        </SelectionWrapper>
        <OverflowButton
          dataPopupSource={`overflow-menu-${item.id}`}
          icon="navigation.overflow"
          onClick={handleOverflow}
        />
      </Box>
    </Wrapper>
  );
};

export default CanvasHeading;
